import React from "react";
import { Link, useParams } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { FiArrowLeft, FiCheckCircle, FiClock, FiMapPin, FiTag } from "react-icons/fi";
import SEO from "../components/SEO";
import FAQ from "../components/FAQ";
import PageHero from "../components/PageHero";
import Loader from "../components/Loader";
import { buildWhatsAppLink } from "../utils/whatsapp";
import { imgUrl } from "../utils/api";
import { useServiceDetail } from "../hooks/useServices";
import NotFound from "./NotFound";

export default function ServiceDetail({ type }) {
  const { slug } = useParams();
  const { service, loading, error } = useServiceDetail(slug);

  if (loading) return <Loader />;
  if (error || !service || service.type !== type) return <NotFound />;

  const waLink = buildWhatsAppLink(
    service.person.whatsapp,
    `Namaste 🙏, I would like to enquire about *${service.title}*. Please share pricing, samagri and available dates.`
  );

  const price = service.priceFrom === "Contact" ? "Contact for quote" : `Starts from ₹${service.priceFrom}`;

  return (
    <div>
      <SEO
        title={`${service.title} - ${service.typeLabel}`}
        description={service.desc}
        keywords={`${service.title}, ${service.typeLabel}, Thane, Mumbai`}
        path={`${service.basePath}/${service.slug}`}
      />

      <PageHero eyebrow={service.typeLabel} title={service.title} subtitle={service.desc} />

      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <Link to={service.basePath} className="inline-flex items-center gap-1 text-saffron-600 text-sm font-medium mb-6 hover:gap-2 transition-all">
            <FiArrowLeft /> Back to {service.type === "pooja" ? "Pooja Services" : "Astrology Services"}
          </Link>

          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-start">
            <div>
              <div className="aspect-[16/10] rounded-2xl overflow-hidden bg-gold-50 border border-gold-100 shadow-premium mb-8">
                <img src={imgUrl(service.image)} alt={service.title} className="w-full h-full object-cover" />
              </div>

              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-saffron-50 border border-gold-100 rounded-2xl p-5">
                  <FiTag className="text-saffron-600 mb-2" />
                  <p className="text-xs uppercase tracking-wide text-gray-500">Pricing</p>
                  <p className="font-heading text-maroon-800">{price}</p>
                </div>
                <div className="bg-saffron-50 border border-gold-100 rounded-2xl p-5">
                  <FiClock className="text-saffron-600 mb-2" />
                  <p className="text-xs uppercase tracking-wide text-gray-500">Duration</p>
                  <p className="font-heading text-maroon-800">{service.duration}</p>
                </div>
                <div className="bg-saffron-50 border border-gold-100 rounded-2xl p-5">
                  <FiMapPin className="text-saffron-600 mb-2" />
                  <p className="text-xs uppercase tracking-wide text-gray-500">Available</p>
                  <p className="font-heading text-maroon-800">{service.type === "pooja" ? "Thane & Mumbai" : "Thane & Online"}</p>
                </div>
              </div>

              <div className="prose max-w-none">
                <h2 className="font-heading text-2xl text-maroon-800 mb-3">What This Service Helps With</h2>
                <p className="text-gray-700 leading-relaxed mb-6">{service.desc}</p>
              </div>

              <h3 className="font-heading text-xl text-maroon-800 mb-3">Best For</h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {(service.bestFor || []).map((item) => (
                  <span key={item} className="bg-gold-50 border border-gold-200 text-maroon-700 px-3 py-1.5 rounded-full text-sm">
                    {item}
                  </span>
                ))}
              </div>

              <h3 className="font-heading text-xl text-maroon-800 mb-3">Included Guidance</h3>
              <div className="grid sm:grid-cols-2 gap-3 mb-10">
                {(service.includes || []).map((item) => (
                  <div key={item} className="flex items-center gap-2 bg-white border border-gold-100 rounded-xl px-4 py-3 shadow-sm">
                    <FiCheckCircle className="text-green-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              {(service.faqs && service.faqs.length > 0) && (
                <>
                  <h3 className="font-heading text-xl text-maroon-800 mb-4 text-center">Common Questions</h3>
                  <FAQ items={service.faqs} />
                </>
              )}
            </div>

            <aside className="lg:sticky lg:top-28 bg-gradient-to-br from-gold-50 to-saffron-50 border border-gold-200 rounded-3xl p-7 shadow-premium">
              <p className="text-xs uppercase tracking-[0.18em] text-saffron-700 font-semibold mb-2">Book This Service</p>
              <h2 className="font-heading text-2xl text-maroon-800 mb-3">{service.title}</h2>
              <p className="text-sm text-gray-600 mb-5">{service.priceNote}</p>
              <div className="space-y-2 text-sm text-gray-700 mb-6">
                <p><strong>Expert:</strong> {service.person.name}</p>
                <p><strong>Phone:</strong> {service.person.phoneDisplay}</p>
                <p><strong>Mode:</strong> {service.type === "pooja" ? "In-person ceremony" : "In-person or online consultation"}</p>
              </div>
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-xl font-semibold shadow-premium-lg transition-colors mb-3"
              >
                <FaWhatsapp size={20} /> Enquire on WhatsApp
              </a>
              <Link
                to={`/contact?service=${encodeURIComponent(service.title)}`}
                className="w-full inline-flex items-center justify-center bg-maroon-800 hover:bg-maroon-900 text-gold-100 px-6 py-3 rounded-xl font-semibold transition-colors"
              >
                Request Booking
              </Link>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
